import { useRef } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { X, Download } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { useScrollLock } from "@/lib/use-scroll-lock";

/**
 * A scannable QR code for a sound/combo permalink — for stream overlays,
 * stickers, IRL summoning. Scanning lands on the page where it plays.
 * Centered modal on every screen size; the QR sits on a white quiet-zone
 * box so it scans reliably, and downloads as a PNG.
 */
export function QrModal({ url, title, onClose }: { url: string; title: string; onClose: () => void }) {
  useScrollLock();
  const boxRef = useRef<HTMLDivElement>(null);

  const download = () => {
    const canvas = boxRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `${title.replace(/[^\w]+/g, "-").replace(/^-+|-+$/g, "").toLowerCase() || "wizard-sound"}-qr.png`;
    a.click();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/70 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`QR code for ${title}`}
        className="relative w-full max-w-xs rounded-2xl border border-mist bg-slate p-5 text-center shadow-2xl"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 rounded-lg p-1 text-fog transition-colors hover:bg-mist/40 hover:text-parchment"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="mb-1 truncate px-4 font-display text-base font-semibold tracking-display text-parchment">
          {title}
        </div>
        <div className="mb-4 font-mono text-[11px] uppercase tracking-wider text-fog">Scan to play</div>

        <div ref={boxRef} className="mx-auto mb-4 w-fit rounded-xl bg-white p-3">
          <QRCodeCanvas value={url} size={200} bgColor="#ffffff" fgColor="#0a0e1a" level="M" marginSize={2} />
        </div>

        <button
          onClick={download}
          className="inline-flex items-center gap-2 rounded-xl bg-spark px-4 py-2 text-sm font-semibold text-ink transition-transform active:scale-95"
        >
          <Download className="h-4 w-4" /> Download PNG
        </button>
      </motion.div>
    </div>,
    document.body,
  );
}
